import { COMMS_CREDIT_COST_INR } from "../constants";

export function daysLeft(isoDate: string): number {
    return Math.max(0, Math.ceil((new Date(isoDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
}

/** Format a paise amount as an Indian-locale rupee string, e.g. 4500 -> "₹45.00". */
export function formatPaiseAsInr(paise: number): string {
    return `₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Whole notification credits (1 credit = one WhatsApp/SMS send = COMMS_CREDIT_COST_INR) left in a paise balance. */
export function paiseToCredits(paise: number): number {
    return Math.floor(paise / (COMMS_CREDIT_COST_INR * 100));
}

/** Format a paise balance as remaining notification credits, e.g. 9750 -> "195 credits". */
export function formatPaiseAsCredits(paise: number): string {
    return `${paiseToCredits(paise).toLocaleString("en-IN")} credits`;
}

/**
 * Subscription pricing is quoted ex-GST, so `price_paise` is NOT what the
 * customer is charged. Razorpay collects `total_payable_paise`.
 *
 * Every price shown to a customer goes through one of these two helpers, so the
 * quoted figure and the checkout figure can never drift apart again: the plans
 * page previously rendered price_paise while Checkout asked for the gross,
 * which reads as an unexplained 18% jump at the payment screen.
 */
export interface PlanPricing {
    price_paise: number;
    gst_paise: number;
    gst_rate_pct: number;
    total_payable_paise: number;
    interval?: string;
}

/** Headline price, always the amount actually charged. e.g. "₹17,108.82". */
export function formatPlanTotal(plan: PlanPricing): string {
    const total = plan.total_payable_paise || plan.price_paise;
    return formatPaiseAsInr(total);
}

/** Sub-line making the tax explicit, e.g. "₹14,499.00 + ₹2,609.82 GST (18%)". */
export function formatPlanTaxBreakdown(plan: PlanPricing): string | null {
    if (!plan.gst_paise) return null;
    return `${formatPaiseAsInr(plan.price_paise)} + ${formatPaiseAsInr(plan.gst_paise)} GST (${plan.gst_rate_pct}%)`;
}

/**
 * The plan is quoted ex-GST, so the headline is the quoted figure and the tax
 * is carried on its own line. Pair `formatPlanBase` with `formatPlanGstLine`:
 * the headline alone understates what Checkout charges, which is the gap the
 * tax line exists to close.
 */

/** Headline price as quoted, excluding tax. e.g. "₹14,499.00". */
export function formatPlanBase(plan: PlanPricing): string {
    return formatPaiseAsInr(plan.price_paise);
}

/** Tax line to sit beneath an ex-GST headline, e.g. "+ ₹2,609.82 GST (18%)". */
export function formatPlanGstLine(plan: PlanPricing): string | null {
    if (!plan.gst_paise) return null;
    return `+ ${formatPaiseAsInr(plan.gst_paise)} GST (${plan.gst_rate_pct}%)`;
}

const INVOICE_STATUS_CONFIG: Record<string, { dot: string; text: string; label: string }> = {
  paid:           { dot: "bg-emerald-500", text: "text-emerald-700", label: "Paid" },
  sent:           { dot: "bg-blue-500",    text: "text-blue-700",    label: "Sent" },
  overdue:        { dot: "bg-red-500",     text: "text-red-700",     label: "Overdue" },
  partially_paid: { dot: "bg-amber-500",   text: "text-amber-700",   label: "Partially paid" },
  draft:          { dot: "bg-slate-400",   text: "text-slate-500",   label: "Draft" },
};

/** Status chip for patient invoice lists. Failed online attempts override unpaid labels. */
export function getInvoiceStatusPresentation(inv: {
  status: string;
  last_payment_failed_at?: string | null;
  last_payment_failure_reason?: string | null;
  payment_attempt_count?: number;
}): { dot: string; text: string; label: string; title?: string } {
  if (inv.last_payment_failed_at && inv.status !== "paid") {
    const reason = inv.last_payment_failure_reason?.trim();
    const attempts = inv.payment_attempt_count ?? 0;
    const title = [
      reason || "Online payment failed",
      attempts > 1 ? `${attempts} failed attempts` : null,
    ].filter(Boolean).join(" · ");
    return { dot: "bg-red-500", text: "text-red-700", label: "Payment failed", title };
  }
  return INVOICE_STATUS_CONFIG[inv.status] ?? INVOICE_STATUS_CONFIG.draft;
}
